/**
 * Utility functions for formatting data
 */

/**
 * Convert email to username
 * Example: "maulik.s@iqm.com" -> "Maulik S"
 */
export const emailToUsername = (email: string): string => {
  if (!email) return '';
  
  // Extract the local part (before @)
  const localPart = email.split('@')[0];
  
  // Split by dots and capitalize each part
  const parts = localPart.split('.');
  const username = parts
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
  
  return username;
};

/**
 * Format date to readable format
 * Example: "2024-01-01T00:00:00Z" -> "1/1/2024"
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  } catch {
    return dateString;
  }
};

/**
 * Format date and time
 * Example: "2024-01-01T10:30:00Z" -> "1/1/2024, 10:30 AM"
 */
export const formatDateTime = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch {
    return dateString;
  }
};

/**
 * Jira ticket pattern — matches external project keys like PLANNER-975, BUGS-123, HCP-35
 * Requires at least 3 uppercase letters to exclude internal IDs like TC-00123, TS-00874
 */
const JIRA_TICKET_RE = /\b([A-Z][A-Z0-9]{2,}-\d+)\b/g;

const JIRA_BASE_URL = 'https://inappad.atlassian.net';

/**
 * Returns true if the folder name contains at least one Jira ticket key.
 */
export function hasJiraTicket(name: string): boolean {
  JIRA_TICKET_RE.lastIndex = 0;
  return JIRA_TICKET_RE.test(name);
}

/**
 * Given a folder name, returns an array of segments — plain strings and
 * { ticket, url } objects — so the caller can render links inline.
 */
export function parseJiraTickets(
  name: string
): Array<string | { ticket: string; url: string }> {
  const result: Array<string | { ticket: string; url: string }> = [];
  let lastIndex = 0;
  JIRA_TICKET_RE.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = JIRA_TICKET_RE.exec(name)) !== null) {
    if (match.index > lastIndex) {
      result.push(name.slice(lastIndex, match.index));
    }
    result.push({
      ticket: match[1],
      url: `${JIRA_BASE_URL}/browse/${match[1]}`,
    });
    lastIndex = JIRA_TICKET_RE.lastIndex;
  }

  if (lastIndex < name.length) {
    result.push(name.slice(lastIndex));
  }

  return result;
}
